import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Rating,
  Badge,
  Skeleton,
  Stack,
  Paper,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CoachingApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  CANONICAL_MENTORS,
  extractMentorsFromResponse,
  isBookableMentor,
  mentorRatingValue,
  resolveDisplayMentors,
} from '../../data/founderMentor';
import QoreConnectPageHero from './QoreConnectPageHero';
import { PAGE_META } from './qoreConnectConfig';
import './QoreConnectPages.css';

// ─── Avatar colour helper ─────────────────────────────────────────────────────
const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#06b6d4', '#ef4444', '#84cc16',
];
function stringAvatar(name = '') {
  const parts = name.trim().split(' ');
  const initials = parts.length > 1
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`
    : (parts[0]?.[0] || '?');
  const color = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  return { sx: { bgcolor: color, width: 80, height: 80, fontSize: 26, fontWeight: 700 }, children: initials.toUpperCase() };
}

// ─── Availability dot ─────────────────────────────────────────────────────────
function AvailabilityBadge({ available }) {
  return (
    <Chip
      size="small"
      label={available ? 'Online' : 'Busy'}
      sx={{
        bgcolor: available ? 'success.main' : 'warning.main',
        color: 'white',
        fontWeight: 600,
        fontSize: 11,
        height: 20,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

// ─── Mentor card ──────────────────────────────────────────────────────────────
function MentorCard({ mentor, onBook }) {
  const theme = useTheme();
  const fullName =
    mentor.name ||
    `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() ||
    'Mentor';
  const photo = mentor.profileImage || mentor.avatar;
  const expertise = mentor.expertiseAreas || mentor.expertise || [];
  const rating = mentorRatingValue(mentor, 4.7);
  const sessions = mentor.totalSessions ?? mentor.sessionsCompleted ?? 0;
  const available = mentor.availability?.isAvailable ?? true;
  const profileUrl = mentor.profileUrl;
  const title = mentor.title || mentor.headline || 'Expert Mentor';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        bgcolor: 'rgba(255,255,255,0.025)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={photo}
              {...(!photo ? stringAvatar(fullName) : {})}
              sx={{ width: 72, height: 72, fontSize: 24, fontWeight: 700 }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {fullName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {title}
              </Typography>
            </Box>
          </Stack>
          <AvailabilityBadge available={available} />
        </Stack>

        {/* Rating + sessions */}
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Rating
              value={rating}
              precision={0.1}
              size="small"
              readOnly
              emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
            />
            <Typography variant="caption" fontWeight={600}>
              {Number(rating).toFixed(1)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {sessions} sessions
            </Typography>
          </Stack>
        </Stack>

        {/* Expertise chips */}
        {expertise.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {expertise.slice(0, 4).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: 11 }}
              />
            ))}
            {expertise.length > 4 && (
              <Chip label={`+${expertise.length - 4}`} size="small" />
            )}
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        {profileUrl ? (
          <Button
            component="a"
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<OpenInNewIcon />}
            sx={{ flexShrink: 0 }}
          >
            View Profile
          </Button>
        ) : <span />}
        <Button
          variant="contained"
          size="small"
          startIcon={<EventAvailableIcon />}
          onClick={() => onBook(mentor)}
          disabled={!isBookableMentor(mentor)}
          sx={{ flexShrink: 0 }}
        >
          {isBookableMentor(mentor) ? 'Request session' : 'Not bookable yet'}
        </Button>
      </CardActions>
    </Card>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function MentorSkeleton() {
  return (
    <Card elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}>
      <CardContent>
        <Stack direction="row" spacing={2} mb={2}>
          <Skeleton variant="circular" width={72} height={72} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Stack>
        <Skeleton variant="text" width="80%" />
        <Stack direction="row" spacing={0.5} mt={1}>
          {[70, 80, 60].map((w) => <Skeleton key={w} variant="rounded" width={w} height={22} />)}
        </Stack>
      </CardContent>
      <Divider />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Skeleton variant="rounded" width={80} height={30} />
        <Skeleton variant="rounded" width={110} height={30} />
      </Box>
    </Card>
  );
}

const EXPERTISE_FILTERS = ['All', 'Engineering', 'Aerospace', 'AI', 'Leadership', 'Career', 'Research'];

// ─── Main page ────────────────────────────────────────────────────────────────
const defaultWhen = () => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Mentorship = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const meta = PAGE_META.mentorship;
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();

  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expertiseFilter, setExpertiseFilter] = useState('All');
  const [bookingMentor, setBookingMentor] = useState(null);
  const [bookingTopic, setBookingTopic] = useState('');
  const [bookingWhen, setBookingWhen] = useState(defaultWhen);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingMsg, setBookingMsg] = useState('');

  // Fetch mentors
  const fetchMentors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CoachingApi.getMentors({ limit: 50 });
      setMentors(resolveDisplayMentors(extractMentorsFromResponse(res)));
    } catch {
      setMentors(CANONICAL_MENTORS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMentors(); }, [fetchMentors]);

  // Filter
  const filtered = mentors.filter((m) => {
    const name = (m.name || `${m.firstName || ''} ${m.lastName || ''}`).toLowerCase();
    const expertise = (m.expertiseAreas || m.expertise || []).join(' ').toLowerCase();
    const matchSearch = !searchQuery || name.includes(searchQuery.toLowerCase()) || expertise.includes(searchQuery.toLowerCase());
    const matchExpertise = expertiseFilter === 'All' || expertise.includes(expertiseFilter.toLowerCase());
    return matchSearch && matchExpertise;
  });

  return (
    <div className="qc-page qc-page--mentorship">
      <QoreConnectPageHero accent={meta.accent} {...meta}>
        <div className="qc-hero-search">
          <input
            type="search"
            className="qc-search-input"
            placeholder={t('qoreConnect.pages.mentorship.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t('qoreConnect.pages.mentorship.searchPlaceholder')}
          />
        </div>
      </QoreConnectPageHero>

      <nav className="qc-page-toolbar" aria-label={t('qoreConnect.pages.mentorship.filters')}>
        <div className="qc-page-toolbar__inner">
          {EXPERTISE_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={`qc-filter-pill ${expertiseFilter === f ? 'qc-filter-pill--active' : ''}`}
              onClick={() => setExpertiseFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </nav>

      <div className="qc-page-body">
        <p className="qc-results-count">
          {loading
            ? t('qoreConnect.pages.mentorship.loading')
            : t('qoreConnect.pages.mentorship.results', { count: filtered.length })}
        </p>
        <Grid container spacing={3}>
          {loading
            ? Array.from({ length: CANONICAL_MENTORS.length }).map((_, i) => (
                <Grid item xs={12} sm={6} lg={4} key={i}>
                  <MentorSkeleton />
                </Grid>
              ))
            : filtered.length === 0
            ? (
                <Grid item xs={12}>
                  <div className="qc-empty-state">
                    <PeopleIcon sx={{ fontSize: 48, color: '#64748b' }} />
                    <h3>{t('qoreConnect.pages.mentorship.emptyTitle')}</h3>
                    <p>{t('qoreConnect.pages.mentorship.emptyDesc')}</p>
                  </div>
                </Grid>
              )
            : filtered.map((mentor) => (
                <Grid item xs={12} sm={6} lg={4} key={mentor._id || mentor.id}>
                  <MentorCard
                    mentor={mentor}
                    onBook={(m) => {
                      if (!isAuthenticated) {
                        navigate('/login', { state: { notice: 'Sign in to request a mentoring session.' } });
                        return;
                      }
                      setBookingMentor(m);
                      setBookingTopic('');
                      setBookingWhen(defaultWhen());
                      setBookingMsg('');
                    }}
                  />
                </Grid>
              ))}
        </Grid>
      </div>

      {bookingMentor && (
        <Paper elevation={6} className="qc-booking-toast" sx={{ flexWrap: 'wrap', gap: 1.5, maxWidth: 560 }}>
          <VideocamIcon sx={{ color: '#a78bfa' }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Request a session with {bookingMentor.name || `${bookingMentor.firstName} ${bookingMentor.lastName}`}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="What do you want to work on?"
              value={bookingTopic}
              onChange={(e) => setBookingTopic(e.target.value)}
              sx={{ mt: 1, mb: 1 }}
            />
            <TextField
              size="small"
              type="datetime-local"
              fullWidth
              value={bookingWhen}
              onChange={(e) => setBookingWhen(e.target.value)}
            />
            {bookingMsg && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {bookingMsg}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="contained"
            disabled={bookingBusy || !bookingTopic.trim()}
            onClick={async () => {
              setBookingBusy(true);
              setBookingMsg('');
              try {
                const res = await CoachingApi.requestSession(token, {
                  mentorId: bookingMentor._id,
                  topic: bookingTopic,
                  title: bookingTopic.slice(0, 200),
                  scheduledDate: new Date(bookingWhen).toISOString(),
                });
                setBookingMsg(res.message || 'Request sent.');
                setTimeout(() => setBookingMentor(null), 1200);
              } catch (err) {
                setBookingMsg(err.message || 'Could not send the request.');
              } finally {
                setBookingBusy(false);
              }
            }}
          >
            {bookingBusy ? 'Sending…' : 'Send'}
          </Button>
          <Button size="small" onClick={() => setBookingMentor(null)}>
            {t('qoreConnect.close')}
          </Button>
        </Paper>
      )}
    </div>
  );
};

export default Mentorship;
